import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave, FiEye, FiEyeOff } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function PaymentSettingsModal({ isOpen, onClose, website, onSuccess }) {
  const [formData, setFormData] = useState({
    acceptPayments: false,
    paymentGateway: 'paystack',
    // Paystack fields
    paystackPublicKey: '',
    paystackSecretKey: '',
    // Stripe fields
    stripePublishableKey: '',
    stripeSecretKey: '',
    currency: 'NGN',
    showSecretKey: false
  })
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    if (website && isOpen) {
      const gateway = website.paymentSettings?.paymentGateway || 'paystack'
      setFormData({
        acceptPayments: website.paymentSettings?.acceptPayments || false,
        paymentGateway: gateway,
        paystackPublicKey: website.paymentSettings?.paystackPublicKey || '',
        paystackSecretKey: website.paymentSettings?.paystackSecretKey || '',
        stripePublishableKey: website.paymentSettings?.stripePublishableKey || '',
        stripeSecretKey: website.paymentSettings?.stripeSecretKey || '',
        currency: website.paymentSettings?.currency || (gateway === 'stripe' ? 'USD' : 'NGN'),
        showSecretKey: false
      })
    }
  }, [website, isOpen])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleGatewayChange = (gateway) => {
    setFormData(prev => ({
      ...prev,
      paymentGateway: gateway,
      currency: gateway === 'stripe' ? 'USD' : 'NGN',
      // Clear the other gateway's keys when switching
      ...(gateway === 'paystack' ? {
        stripePublishableKey: '',
        stripeSecretKey: ''
      } : {
        paystackPublicKey: '',
        paystackSecretKey: ''
      })
    }))
  }

  const handleToggleShowSecret = () => {
    setFormData(prev => ({
      ...prev,
      showSecretKey: !prev.showSecretKey
    }))
  }

  const handleTestPaymentConnection = async () => {
    const gateway = formData.paymentGateway
    let testData = { paymentGateway: gateway }

    if (gateway === 'paystack') {
      if (!formData.paystackPublicKey || !formData.paystackSecretKey) {
        toast.error('Please enter both Paystack keys')
        return
      }
      testData.paystackPublicKey = formData.paystackPublicKey
      testData.paystackSecretKey = formData.paystackSecretKey
    } else if (gateway === 'stripe') {
      if (!formData.stripePublishableKey || !formData.stripeSecretKey) {
        toast.error('Please enter both Stripe keys')
        return
      }
      testData.stripePublicKey = formData.stripePublishableKey
      testData.stripeSecretKey = formData.stripeSecretKey
    }

    setTesting(true)
    try {
      const response = await axios.post(`/websites/${website._id}/test-payment-keys`, testData)
      if (response.data.success) {
        toast.success(`${gateway.charAt(0).toUpperCase() + gateway.slice(1)} keys are valid!`)
      } else {
        toast.error(response.data.message || 'Invalid payment keys')
      }
    } catch (error) {
      console.error('Error testing payment keys:', error)
      toast.error(error.response?.data?.message || 'Failed to test payment keys')
    } finally {
      setTesting(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (formData.acceptPayments) {
      const gateway = formData.paymentGateway
      if (gateway === 'paystack') {
        if (!formData.paystackPublicKey.trim()) {
          toast.error('Paystack public key is required')
          return
        }
        if (!formData.paystackSecretKey.trim()) {
          toast.error('Paystack secret key is required')
          return
        }
      } else if (gateway === 'stripe') {
        if (!formData.stripePublishableKey.trim()) {
          toast.error('Stripe publishable key is required')
          return
        }
        if (!formData.stripeSecretKey.trim()) {
          toast.error('Stripe secret key is required')
          return
        }
      }
    }

    setSaving(true)
    const submitData = {
      acceptPayments: formData.acceptPayments,
      paymentGateway: formData.paymentGateway,
      currency: formData.currency
    }

    // Add gateway-specific keys
    if (formData.paymentGateway === 'paystack') {
      submitData.paystackPublicKey = formData.paystackPublicKey
      submitData.paystackSecretKey = formData.paystackSecretKey
    } else if (formData.paymentGateway === 'stripe') {
      submitData.stripePublicKey = formData.stripePublishableKey
      submitData.stripeSecretKey = formData.stripeSecretKey
    }

    axios.put(`/websites/${website._id}/payment-settings`, submitData)
      .then(() => {
        toast.success('Payment settings saved successfully')
        onSuccess()
      })
      .catch(error => {
        console.error('Error saving payment settings:', error)
        toast.error(error.response?.data?.message || 'Failed to save payment settings')
      })
      .finally(() => {
        setSaving(false)
      })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">Payment Settings</h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Security Banner */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-3">
            <p className="text-sm font-semibold text-blue-900">🔒 Security & Encryption</p>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li>All Paystack credentials are encrypted at rest using AES-256-GCM</li>
              <li>Credentials are never exposed in logs, responses, or to administrators</li>
              <li>Only the backend uses credentials during payment processing</li>
              <li>Encryption keys are stored as environment variables</li>
              <li>Frontend never receives or stores sensitive payment credentials</li>
            </ul>
          </div>

          {/* Enable Payments */}
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="acceptPayments"
                checked={formData.acceptPayments}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-gray-300 cursor-pointer"
              />
              <span className="text-lg font-semibold text-gray-900">Enable Payment Processing</span>
            </label>
            <p className="text-sm text-gray-600 ml-8">
              Allow customers to make payments on your website using Paystack. Once enabled, all website categories with checkout functionality will accept payments seamlessly.
            </p>
          </div>

          {/* Payment Settings (shown if enabled) */}
          {formData.acceptPayments && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              {/* Payment Gateway Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Payment Gateway
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleGatewayChange('paystack')}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      formData.paymentGateway === 'paystack'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="font-semibold">Paystack</div>
                    <div className="text-xs mt-1 opacity-75">Africa-focused</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGatewayChange('stripe')}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      formData.paymentGateway === 'stripe'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="font-semibold">Stripe</div>
                    <div className="text-xs mt-1 opacity-75">Global payments</div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Choose your payment processor. Only one gateway can be active at a time.
                </p>
              </div>

              {/* Gateway-specific settings */}
              {formData.paymentGateway === 'paystack' ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900">Paystack Credentials</h3>

                  {/* Warning Box */}
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-red-900 mb-2">⚠️ Important Security Notice</p>
                    <ul className="text-xs text-red-800 space-y-1 list-disc list-inside">
                      <li>Never share your secret key with anyone</li>
                      <li>Do not commit credentials to version control</li>
                      <li>Rotate keys periodically if compromised</li>
                      <li>The secret key is not stored in unencrypted form</li>
                      <li>If you change keys, update them here immediately</li>
                    </ul>
                  </div>

                  {/* Currency Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="input-field w-full"
                    >
                      <option value="NGN">Nigerian Naira (NGN)</option>
                      <option value="GHS">Ghana Cedi (GHS)</option>
                      <option value="KES">Kenya Shilling (KES)</option>
                      <option value="ZAR">South African Rand (ZAR)</option>
                      <option value="USD">US Dollar (USD)</option>
                    </select>
                  </div>

                  {/* Public Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Paystack Public Key *
                    </label>
                    <input
                      type="password"
                      name="paystackPublicKey"
                      value={formData.paystackPublicKey}
                      onChange={handleInputChange}
                      placeholder="pk_live_xxxxxxxx or pk_test_xxxxxxxx"
                      className="input-field w-full"
                      required={formData.acceptPayments && formData.paymentGateway === 'paystack'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Get this from your Paystack dashboard settings
                    </p>
                  </div>

                  {/* Secret Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Paystack Secret Key *
                    </label>
                    <div className="relative">
                      <input
                        type={formData.showSecretKey ? 'text' : 'password'}
                        name="paystackSecretKey"
                        value={formData.paystackSecretKey}
                        onChange={handleInputChange}
                        placeholder="sk_live_xxxxxxxx or sk_test_xxxxxxxx"
                        className="input-field w-full pr-10"
                        required={formData.acceptPayments && formData.paymentGateway === 'paystack'}
                      />
                      <button
                        type="button"
                        onClick={handleToggleShowSecret}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {formData.showSecretKey ? (
                          <FiEyeOff className="w-4 h-4" />
                        ) : (
                          <FiEye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Never share your secret key. Keep it secure.
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg space-y-2">
                    <p className="text-sm font-semibold text-yellow-900">ℹ️ How to Get Your Credentials</p>
                    <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
                      <li>Go to <a href="https://dashboard.paystack.com/settings/developer" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline font-semibold">Paystack Dashboard → Settings → Developer</a></li>
                      <li>Copy your <strong>Public Key</strong> (starts with pk_)</li>
                      <li>Copy your <strong>Secret Key</strong> (starts with sk_)</li>
                      <li>Paste them here and test the connection</li>
                    </ol>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900">Stripe Credentials</h3>

                  {/* Warning Box */}
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-red-900 mb-2">⚠️ Important Security Notice</p>
                    <ul className="text-xs text-red-800 space-y-1 list-disc list-inside">
                      <li>Never share your secret key with anyone</li>
                      <li>Do not commit credentials to version control</li>
                      <li>Rotate keys periodically if compromised</li>
                      <li>The secret key is not stored in unencrypted form</li>
                      <li>If you change keys, update them here immediately</li>
                    </ul>
                  </div>

                  {/* Currency Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="input-field w-full"
                    >
                      <option value="USD">US Dollar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                      <option value="GBP">British Pound (GBP)</option>
                      <option value="CAD">Canadian Dollar (CAD)</option>
                      <option value="AUD">Australian Dollar (AUD)</option>
                      <option value="JPY">Japanese Yen (JPY)</option>
                      <option value="CHF">Swiss Franc (CHF)</option>
                      <option value="SEK">Swedish Krona (SEK)</option>
                      <option value="NOK">Norwegian Krone (NOK)</option>
                      <option value="DKK">Danish Krone (DKK)</option>
                      <option value="NZD">New Zealand Dollar (NZD)</option>
                      <option value="MXN">Mexican Peso (MXN)</option>
                      <option value="SGD">Singapore Dollar (SGD)</option>
                      <option value="HKD">Hong Kong Dollar (HKD)</option>
                      <option value="BRL">Brazilian Real (BRL)</option>
                      <option value="ZAR">South African Rand (ZAR)</option>
                      <option value="THB">Thai Baht (THB)</option>
                      <option value="MYR">Malaysian Ringgit (MYR)</option>
                      <option value="AED">UAE Dirham (AED)</option>
                      <option value="PLN">Polish Złoty (PLN)</option>
                      <option value="CZK">Czech Koruna (CZK)</option>
                      <option value="HUF">Hungarian Forint (HUF)</option>
                      <option value="ILS">Israeli Shekel (ILS)</option>
                      <option value="RON">Romanian Leu (RON)</option>
                      <option value="BGN">Bulgarian Lev (BGN)</option>
                      <option value="HRK">Croatian Kuna (HRK)</option>
                      <option value="ISK">Icelandic Króna (ISK)</option>
                      <option value="TRY">Turkish Lira (TRY)</option>
                      <option value="RUB">Russian Ruble (RUB)</option>
                    </select>
                  </div>

                  {/* Publishable Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stripe Publishable Key *
                    </label>
                    <input
                      type="password"
                      name="stripePublishableKey"
                      value={formData.stripePublishableKey}
                      onChange={handleInputChange}
                      placeholder="pk_live_xxxxxxxx or pk_test_xxxxxxxx"
                      className="input-field w-full"
                      required={formData.acceptPayments && formData.paymentGateway === 'stripe'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Get this from your Stripe dashboard settings
                    </p>
                  </div>

                  {/* Secret Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stripe Secret Key *
                    </label>
                    <div className="relative">
                      <input
                        type={formData.showSecretKey ? 'text' : 'password'}
                        name="stripeSecretKey"
                        value={formData.stripeSecretKey}
                        onChange={handleInputChange}
                        placeholder="sk_live_xxxxxxxx or sk_test_xxxxxxxx"
                        className="input-field w-full pr-10"
                        required={formData.acceptPayments && formData.paymentGateway === 'stripe'}
                      />
                      <button
                        type="button"
                        onClick={handleToggleShowSecret}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {formData.showSecretKey ? (
                          <FiEyeOff className="w-4 h-4" />
                        ) : (
                          <FiEye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Never share your secret key. Keep it secure.
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg space-y-2">
                    <p className="text-sm font-semibold text-yellow-900">ℹ️ How to Get Your Credentials</p>
                    <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
                      <li>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline font-semibold">Stripe Dashboard → Developers → API Keys</a></li>
                      <li>Copy your <strong>Publishable Key</strong> (starts with pk_)</li>
                      <li>Copy your <strong>Secret Key</strong> (starts with sk_)</li>
                      <li>Paste them here and test the connection</li>
                    </ol>
                  </div>
                </>
              )}

              {/* Test Connection */}
              <button
                type="button"
                onClick={handleTestPaymentConnection}
                disabled={testing || (formData.paymentGateway === 'paystack' && (!formData.paystackPublicKey || !formData.paystackSecretKey)) || (formData.paymentGateway === 'stripe' && (!formData.stripePublishableKey || !formData.stripeSecretKey))}
                className="w-full py-2 px-4 bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
              >
                {testing ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" /> Testing...
                  </>
                ) : (
                  `Test ${formData.paymentGateway === 'paystack' ? 'Paystack' : 'Stripe'} Connection`
                )}
              </button>

              {/* Auto-Apply Info */}
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg space-y-2">
                <p className="text-sm font-semibold text-green-900">✓ Automatic Application</p>
                <p className="text-xs text-green-800">
                  Once configured and saved, these {formData.paymentGateway === 'paystack' ? 'Paystack' : 'Stripe'} credentials will be automatically applied to <strong>all your website categories with checkout functionality</strong> (e-commerce, booking, event, etc.). No additional configuration needed per category.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (formData.acceptPayments && (
                (formData.paymentGateway === 'paystack' && (!formData.paystackPublicKey || !formData.paystackSecretKey)) ||
                (formData.paymentGateway === 'stripe' && (!formData.stripePublishableKey || !formData.stripeSecretKey))
              ))}
              className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" /> Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
